import React, { useEffect, useRef, useState } from 'react';

const CustomCursor = () => {
  const [isMobile, setIsMobile] = useState(false);
  const cursorRef = useRef(null);
  const followerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mouseRef = useRef({ x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0, y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0 });
  const followerPosRef = useRef({ x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0, y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0 });
  const isHoveringRef = useRef(false);
  const isActiveRef = useRef(false);

  // Verificar se é mobile e adicionar listener de resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      return mobile;
    };

    // Verificar na montagem
    checkMobile();

    // Adicionar listener de resize
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    // Verificar se é mobile antes de inicializar
    if (isMobile || (typeof window !== 'undefined' && window.innerWidth <= 768)) {
      return;
    }

    // Função helper para obter elementos atualizados (sempre busca do DOM)
    const getElements = () => {
      return {
        cursor: cursorRef.current || document.querySelector('.custom-cursor'),
        follower: followerRef.current || document.querySelector('.cursor-follower')
      };
    };

    // Inicializar posições
    const initPositions = () => {
      const { cursor, follower } = getElements();
      if (cursor && follower) {
        followerPosRef.current.x = mouseRef.current.x;
        followerPosRef.current.y = mouseRef.current.y;
        isActiveRef.current = true;
        return true;
      }
      return false;
    };

    // Aguardar um frame para garantir que os elementos estão no DOM
    requestAnimationFrame(() => {
      if (!initPositions()) {
        // Se ainda não estiverem prontos, tentar novamente após um pequeno delay
        setTimeout(() => {
          if (!initPositions()) {
            return;
          }
        }, 100);
      }
    });

    // Handler global para mousemove - sempre captura a posição real do mouse
    const handleMouseMove = (e) => {
      // Sempre atualizar a posição do mouse, independente dos elementos DOM
      const x = e.clientX || 0;
      const y = e.clientY || 0;
      mouseRef.current.x = x;
      mouseRef.current.y = y;

      // Buscar elementos atualizados a cada movimento (garante que está sempre atualizado)
      const { cursor, follower } = getElements();

      // Verificar hover de forma mais robusta
      let target = e.target;
      let isInteractive = false;

      // Verificar se o target ou algum parent é interativo
      while (target && target !== document.body) {
        if (target.matches && target.matches('a, button, .service-card, .project-card, .about-card, .nav-link, .btn-primary, .btn-secondary')) {
          isInteractive = true;
          break;
        }
        target = target.parentElement;
      }

      // Atualizar hover apenas se mudou
      if (isInteractive !== isHoveringRef.current) {
        isHoveringRef.current = isInteractive;
        if (cursor) {
          cursor.classList.toggle('hover', isInteractive);
        }
        if (follower) {
          follower.classList.toggle('hover', isInteractive);
        }
      }

      // Atualizar cursor principal - aplicar translate e scale juntos
      if (cursor) {
        const scale = isInteractive ? 0.5 : 1;
        cursor.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
        cursor.style.left = '0';
        cursor.style.top = '0';
      }
    };

    // Animação suave do follower - usa elementos atualizados a cada frame
    const animateFollower = () => {
      if (!isActiveRef.current) {
        animationFrameRef.current = requestAnimationFrame(animateFollower);
        return;
      }

      // Buscar follower atualizado a cada frame
      const follower = followerRef.current || document.querySelector('.cursor-follower');
      
      if (!follower) {
        animationFrameRef.current = requestAnimationFrame(animateFollower);
        return;
      }

      const distX = mouseRef.current.x - followerPosRef.current.x;
      const distY = mouseRef.current.y - followerPosRef.current.y;

      // Só atualizar se houver movimento significativo
      if (Math.abs(distX) > 0.01 || Math.abs(distY) > 0.01) {
        followerPosRef.current.x += distX / 10;
        followerPosRef.current.y += distY / 10;

        // Aplicar translate e scale juntos
        const scale = isHoveringRef.current ? 1.5 : 1;
        follower.style.transform = `translate(${followerPosRef.current.x}px, ${followerPosRef.current.y}px) scale(${scale})`;
        follower.style.left = '0';
        follower.style.top = '0';
      }

      animationFrameRef.current = requestAnimationFrame(animateFollower);
    };

    // Adicionar listener no document com capture para garantir que sempre capture
    document.addEventListener('mousemove', handleMouseMove, { passive: true, capture: true });
    
    // Também adicionar no window como fallback
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    
    // Iniciar animação após um pequeno delay para garantir que os elementos estão prontos
    const startAnimation = () => {
      requestAnimationFrame(() => {
        animateFollower();
      });
    };
    
    // Aguardar um frame antes de iniciar
    requestAnimationFrame(startAnimation);

    return () => {
      isActiveRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isMobile]);

  // Não renderizar em mobile
  if (isMobile || (typeof window !== 'undefined' && window.innerWidth <= 768)) {
    return null;
  }

  return (
    <>
      <div
        ref={cursorRef}
        className="custom-cursor"
      />
      <div
        ref={followerRef}
        className="cursor-follower"
      />
    </>
  );
};

export default CustomCursor;

